import React, { useState } from 'react';

/* ──────────────────────────────────────────────────────────────────
 * BillingCEBanner — a finalized pay app that isn't in ComputerEase
 *
 * Only what the agent itself reported (Duncan 2026-10-30, fix list #21/#24; the old "Needs CE sync" chip was
 * dropped for guessing): waiting on the pay app before it to be posted (server ceWait.js), or a try that failed or
 * was cancelled, with its reason. Either way the PM presses Retry; nothing retries on its own. A pay app that was
 * sent and hasn't come back shows nothing.
 * ────────────────────────────────────────────────────────────────── */

const AlertIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const when = (t) => (t ? new Date(t).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : null);

/** The state a pay app's ComputerEase banner shows: 'waiting', 'failed', or null for none. */
export const ceStateOf = (period) => {
  if (!period || period.status === 'Draft' || period.superseded || Number(period.ce_synced) === 1) return null;
  if (period.ce_wait_app) return 'waiting';
  if (period.ce_sync_error) return 'failed';
  return null;
};

const BillingCEBanner = ({ period, onRetry }) => {
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null); // the server's answer to a Retry
  const [err, setErr] = useState(null);
  const state = ceStateOf(period);
  if (sent) {
    return (
      <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: '12px 18px', fontSize: 13.5, color: 'var(--text-2)' }}>
        {sent}
      </div>
    );
  }
  if (!state) return null;
  const name = period.period_name || 'This pay app';
  const before = `AFP #${period.ce_wait_app}`;
  const tried = when(period.ce_tried_at);
  const retry = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await onRetry(period.id);
      setSent((r && r.message) || `Sent ${name} to ComputerEase. It shows as in ComputerEase once the agent finishes.`);
    } catch (e) {
      setErr(e?.message || 'Retry failed.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div role="status" style={{
      background: 'var(--warning-soft)',
      border: '1px solid color-mix(in oklch, var(--warning) 35%, transparent)',
      borderRadius: 12, padding: '14px 18px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14.5, fontWeight: 600, color: 'var(--warning)' }}>
        <AlertIcon /> {state === 'waiting' ? 'Waiting on ComputerEase' : 'Not in ComputerEase'}
      </div>
      <div style={{ fontSize: 13.5, color: 'var(--text-2)', lineHeight: 1.5, marginTop: 4 }}>
        {state === 'waiting'
          ? `${name} can't go into ComputerEase until ${before} is posted there. Billing it now would overwrite ${before}, so nothing was entered. Once accounting posts ${before}, press Retry.`
          : period.ce_sync_error}
      </div>
      {err && <div role="alert" style={{ fontSize: 13.5, color: 'var(--danger)', marginTop: 6, lineHeight: 1.45 }}>{err}</div>}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10, flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={retry}
          disabled={busy}
          style={{
            height: 32, padding: '0 14px', borderRadius: 8, fontSize: 13.5, fontWeight: 500,
            color: 'var(--text-1)', background: 'var(--bg-surface)', border: '1px solid var(--border-default)',
            cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1, fontFamily: 'inherit',
          }}
        >{busy ? 'Sending…' : state === 'waiting' ? 'Retry now' : 'Retry'}</button>
        {tried && (
          <span style={{ fontSize: 12.5, color: 'var(--text-3)' }}>
            Last tried {tried}{state === 'waiting' ? ` · ComputerEase was still on Application #${period.ce_wait_app}` : ''}
          </span>
        )}
      </div>
    </div>
  );
};

export default BillingCEBanner;
